import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { NavigationInterface } from 'app/layout/components/navigation/models/navigation-interface';
import { NavigationItemInterface } from 'app/layout/components/navigation/models/navigation-item-interface';

import { strCurrentTabLink as CA_InvoiceStrCurrentTabLink } from 'app/pages/customer-area/invoice/invoice.constants';
import { strCurrentTabLink as CA_ProductionFlowStrCurrentTabLink } from 'app/pages/customer-area/production-flow/production-flow.constants';
import { strCurrentTabLink as LA_InvoiceStrCurrentTabLink } from 'app/pages/laboratory-area/invoice/invoice.constants';
import { strCurrentTabLink as LA_ProductionFlowStrCurrentTabLink } from 'app/pages/laboratory-area/production-flow/production-flow.constants';
import { strCurrentTabLink as LA_PurchaseInvoiceStrCurrentTabLink } from 'app/pages/laboratory-area/purchase-invoice/purchase-invoice.constants';
import { Area } from '../../../shared/enums/area.enum';
import { LocalStorageService } from '../local-storage.service';
import { WorkingLaboratoryService } from '../prothesis-api/working-laboratory.service';
import { WorkingCustomerService } from '../prothesis-customer-api/working-customer.service';
import { AreaMenu } from './area-menu.enum';

@Injectable({
  providedIn: 'root',
})
export class LeftMenuService {
  public workingCustomerService: WorkingCustomerService;
  public workingLaboratoryService: WorkingLaboratoryService;

  private _area: Area;
  private _areaMenus: AreaMenu[];
  private _currentFuseNavigation: NavigationInterface[];
  private _fuseNavigationService = inject(FuseNavigationService);
  private _localStorageService = inject(LocalStorageService);
  private _navigationAdmin: NavigationInterface = {
    id: 'admin',
    classes: 'nav-link-admin-area',
    translate: 'NAV.ADMIN',
    type: 'group',
    children: [
      {
        id: 'laboratory',
        classes: 'nav-link-admin-area',
        translate: 'NAV.LABORATORY',
        type: 'item',
        icon: 'svg_laboratory',
        url: '/admin/laboratory',
      },
      {
        id: 'user',
        classes: 'nav-link-admin-area',
        translate: 'NAV.USER',
        type: 'item',
        icon: 'svg_user',
        url: '/admin/user',
      },
    ],
  };
  private _navigationCustomerAndCustomerGroup: NavigationInterface = {
    id: 'customer-area',
    classes: 'nav-link-customer-area',
    hideGroupTitleFlag: true,
    translate: 'NAV.CUSTOMER_AREA',
    type: 'group',
  };
  private _navigationCustomerAndCustomerGroupChildren: NavigationItemInterface[];
  private _navigationCustomerChildRate: NavigationItemInterface = {
    id: 'customer-area-rate',
    classes: 'nav-link-customer-area',
    translate: 'NAV.CUSTOMERAREA.RATE',
    type: 'item',
    icon: 'svg_rate',
    url: '/customer-area/rate',
  };
  private _navigationCustomerChildren: NavigationItemInterface[] = [
    {
      id: 'customer-area-home',
      classes: 'nav-link-customer-area',
      translate: 'NAV.HOME.TITLE',
      type: 'item',
      icon: 'svg_home',
      url: '/customer-area/home',
    },
    {
      id: 'customer-area-production-flow',
      classes: 'nav-link-customer-area',
      translate: 'NAV.CUSTOMERAREA.PRODUCTION',
      type: 'item',
      icon: 'svg_production_flow',
      function: () => {
        const link = this._localStorageService.read(CA_ProductionFlowStrCurrentTabLink);
        if (link) {
          this._router.navigateByUrl(`/customer-area/production-flow/${link}`);
        } else {
          this._router.navigateByUrl(`/customer-area/production-flow/delivery-form`);
        }
        return true;
      },
      url: '/customer-area/production-flow',
    },
    {
      id: 'customer-area-invoice',
      classes: 'nav-link-customer-area',
      translate: 'NAV.INVOICE',
      type: 'item',
      icon: 'svg_invoice',
      function: () => {
        const link = this._localStorageService.read(CA_InvoiceStrCurrentTabLink);
        if (link) {
          this._router.navigateByUrl(`/customer-area/invoice/${link}`);
        } else {
          this._router.navigateByUrl(`/customer-area/invoice`);
        }
        return true;
      },
      url: '/customer-area/invoice',
    },
  ];

  private _navigationCustomerMember: NavigationInterface = {
    id: 'customer-area',
    classes: 'nav-link-customer-area',
    hideGroupTitleFlag: true,
    translate: 'NAV.CUSTOMER_AREA',
    type: 'group',
    children: this._navigationCustomerChildren,
  };

  private _navigationLaboratory: NavigationInterface = {
    id: 'laboratory-area',
    hideGroupTitleFlag: true,
    translate: 'NAV.LABORATORY_AREA',
    type: 'group',
    children: [
      {
        id: 'laboratory-area-home',
        translate: 'NAV.HOME.TITLE',
        type: 'item',
        icon: 'svg_home',
        url: '/laboratory-area/home',
      },
      {
        id: 'directory',
        translate: 'NAV.DIRECTORY',
        type: 'collapsable',
        icon: 'svg_contact',
        children: [
          {
            id: 'customer',
            translate: 'NAV.CUSTOMER',
            type: 'item',
            icon: 'svg_customer',
            url: '/customer',
          },
          {
            id: 'manufacturer',
            translate: 'NAV.MANUFACTURER',
            type: 'item',
            icon: 'svg_manufacturer',
            url: '/manufacturer',
          },
          {
            id: 'resource',
            translate: 'NAV.RESOURCE',
            type: 'item',
            icon: 'svg_laboratory_resource',
            url: '/resource',
          },
        ],
      },
      {
        id: 'catalog',
        translate: 'NAV.CATALOG',
        type: 'collapsable',
        icon: 'svg_catalog',
        children: [
          {
            id: 'item',
            translate: 'NAV.ITEM',
            type: 'item',
            icon: 'svg_item',
            url: '/item',
          },
          {
            id: 'material',
            translate: 'NAV.MATERIAL',
            type: 'item',
            icon: 'svg_raw_material',
            url: '/raw-material',
          },
          {
            id: 'shade',
            translate: 'NAV.SHADE',
            type: 'item',
            icon: 'svg_shade',
            url: '/shade',
          },
        ],
      },
      {
        id: 'rate',
        translate: 'NAV.RATE',
        type: 'item',
        icon: 'svg_rate',
        url: '/rate',
      },
      {
        id: 'production-flow',
        translate: 'NAV.PRODUCTION',
        type: 'item',
        icon: 'svg_production_flow',
        function: () => {
          const link = this._localStorageService.read(LA_ProductionFlowStrCurrentTabLink);
          if (link) {
            this._router.navigateByUrl(`/production-flow/${link}`);
          } else {
            this._router.navigateByUrl(`/production-flow/delivery-form`);
          }
          return true;
        },
        url: '/production-flow',
      },
      {
        id: 'patient',
        translate: 'NAV.PATIENT',
        type: 'item',
        icon: 'svg_patient',
        url: '/patient',
      },
      {
        id: 'invoice',
        translate: 'NAV.INVOICE',
        type: 'item',
        icon: 'svg_invoice',
        function: () => {
          const link = this._localStorageService.read(LA_InvoiceStrCurrentTabLink);
          if (link) {
            this._router.navigateByUrl(`/invoice/${link}`);
          } else {
            this._router.navigateByUrl(`/invoice`);
          }
          return true;
        },
        url: '/invoice',
      },
      {
        id: 'payment',
        translate: 'NAV.PAYMENT',
        type: 'item',
        icon: 'svg_payment',
        function: () => {
          const link = this._localStorageService.read('payment-current-tab-link');
          if (link) {
            this._router.navigateByUrl(`/payment/${link}`);
          } else {
            this._router.navigateByUrl(`/payment`);
          }
          return true;
        },
        url: '/payment',
      },
      {
        id: 'purchase-invoice',
        translate: 'NAV.PURCHASE_INVOICE',
        type: 'item',
        icon: 'svg_purchase_invoice',
        function: () => {
          const link = this._localStorageService.read(LA_PurchaseInvoiceStrCurrentTabLink);
          if (link) {
            this._router.navigateByUrl(`/purchase-invoice/${link}`);
          } else {
            this._router.navigateByUrl(`/purchase-invoice`);
          }
          return true;
        },
        url: '/purchase-invoice',
      },
      {
        id: 'journal',
        translate: 'NAV.JOURNAL',
        type: 'item',
        icon: 'svg_journal',
        url: '/journal',
      },
    ],
  };

  private _navigationNoArea: NavigationInterface = {
    id: 'no-area',
    classes: 'nav-link-no-area',
    translate: 'NAV.HOME.TITLE',
    type: 'item',
    icon: 'svg_home',
    url: '/home',
  };

  private _router = inject(Router);

  public constructor() {
    this._navigationCustomerAndCustomerGroupChildren = Array.from<NavigationItemInterface>(this._navigationCustomerChildren);
    this._navigationCustomerAndCustomerGroupChildren.splice(1, 0, this._navigationCustomerChildRate);
    this._navigationCustomerAndCustomerGroup.children = this._navigationCustomerAndCustomerGroupChildren;

    this._router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        if (event.url.startsWith('/customer-area/')) {
          this._area = Area.Customer;
        } else if (event.url.startsWith('/admin/')) {
          this._area = Area.Admin;
        } else {
          this._area = Area.Laboratory;
        }
      }
    });
  }

  public get area(): Area {
    return this._area;
  }

  public get areaMenus(): AreaMenu[] {
    return this._areaMenus;
  }

  public get currentNavigation(): NavigationInterface[] {
    return this._currentFuseNavigation;
  }

  public getNavigation(
    areaMenus: AreaMenu[],
    laboratoryDemoModeFlag: boolean | null = null,
    adminOrSuperAdminFlag: boolean,
    laboratoryName: string | null = null,
  ): NavigationInterface[] {
    this._areaMenus = areaMenus;
    this._currentFuseNavigation = new Array<NavigationInterface>();

    for (const areaMenu of areaMenus) {
      if (areaMenu === AreaMenu.Laboratory) {
        if (laboratoryName) {
          this._navigationLaboratory.translate = laboratoryName;
        }
        this._navigationLaboratory.hideGroupTitleFlag =
          this.workingLaboratoryService.authService.allowedLaboratoryUuids?.length !== 1 && !this.workingLaboratoryService.exploringFlag;
        this._currentFuseNavigation.push(Object.assign({}, this._navigationLaboratory));

        if (this.workingLaboratoryService.laboratoryRole) {
          this.setVisibilityForLaboratoryItemNavigation(
            this._navigationLaboratory,
            'directory',
            this.workingLaboratoryService.laboratoryRole?.customerModuleFlag ||
              this.workingLaboratoryService.laboratoryRole?.manufacturerModuleFlag ||
              this.workingLaboratoryService.laboratoryRole?.laboratoryResourceModuleFlag,
          );
          const navigationLaboratoryDirectory = this._navigationLaboratory.children!.find((x) => x.id === 'directory');
          this.setVisibilityForLaboratoryItemNavigation(
            navigationLaboratoryDirectory,
            'customer',
            this.workingLaboratoryService.laboratoryRole?.customerModuleFlag,
          );
          this.setVisibilityForLaboratoryItemNavigation(
            navigationLaboratoryDirectory,
            'manufacturer',
            this.workingLaboratoryService.laboratoryRole?.manufacturerModuleFlag,
          );
          this.setVisibilityForLaboratoryItemNavigation(
            navigationLaboratoryDirectory,
            'resource',
            this.workingLaboratoryService.laboratoryRole?.laboratoryResourceModuleFlag,
          );

          this.setVisibilityForLaboratoryItemNavigation(
            this._navigationLaboratory,
            'catalog',
            this.workingLaboratoryService.laboratoryRole?.itemModuleFlag ||
              this.workingLaboratoryService.laboratoryRole?.rawMaterialModuleFlag ||
              this.workingLaboratoryService.laboratoryRole?.shadeModuleFlag,
          );
          const navigationLaboratoryCatalog = this._navigationLaboratory.children!.find((x) => x.id === 'catalog');
          this.setVisibilityForLaboratoryItemNavigation(
            navigationLaboratoryCatalog,
            'item',
            this.workingLaboratoryService.laboratoryRole?.itemModuleFlag,
          );
          this.setVisibilityForLaboratoryItemNavigation(
            navigationLaboratoryCatalog,
            'material',
            this.workingLaboratoryService.laboratoryRole?.rawMaterialModuleFlag,
          );
          this.setVisibilityForLaboratoryItemNavigation(
            navigationLaboratoryCatalog,
            'shade',
            this.workingLaboratoryService.laboratoryRole?.shadeModuleFlag,
          );

          this.setVisibilityForLaboratoryItemNavigation(
            this._navigationLaboratory,
            'rate',
            this.workingLaboratoryService.laboratoryRole?.rateModuleFlag,
          );

          this.setVisibilityForLaboratoryItemNavigation(
            this._navigationLaboratory,
            'production-flow',
            this.workingLaboratoryService.laboratoryRole?.productionFlowModuleFlag,
          );

          this.setVisibilityForLaboratoryItemNavigation(
            this._navigationLaboratory,
            'patient',
            this.workingLaboratoryService.laboratoryRole?.patientModuleFlag,
          );

          this.setVisibilityForLaboratoryItemNavigation(
            this._navigationLaboratory,
            'invoice',
            this.workingLaboratoryService.laboratoryRole?.invoiceModuleFlag,
          );

          this.setVisibilityForLaboratoryItemNavigation(
            this._navigationLaboratory,
            'payment',
            this.workingLaboratoryService.laboratoryRole?.paymentModuleFlag,
          );

          // Purchase-invoice menu item is driven by the dedicated role permission.
          this.setVisibilityForLaboratoryItemNavigation(
            this._navigationLaboratory,
            'purchase-invoice',
            this.workingLaboratoryService.laboratoryRole?.purchaseInvoiceModuleFlag,
          );

          this.setVisibilityForLaboratoryItemNavigation(
            this._navigationLaboratory,
            'journal',
            this.workingLaboratoryService.laboratoryRole?.journalModuleFlag,
          );
        }

        this.refreshClassesForNavigationLaboratory(laboratoryDemoModeFlag, adminOrSuperAdminFlag);
      } else if (areaMenu === AreaMenu.Admin) {
        this._currentFuseNavigation.push(Object.assign({}, this._navigationAdmin));
      } else if (areaMenu === AreaMenu.CustomerAndCustomerGroup) {
        if (laboratoryName) {
          this._navigationCustomerAndCustomerGroup.translate = laboratoryName;
        }
        this._navigationCustomerAndCustomerGroup.text2 = null;
        if (!this.workingCustomerService.exploringFlag) {
          this._navigationCustomerAndCustomerGroup.text2 = this.workingCustomerService.customerName;
        }
        this._navigationCustomerAndCustomerGroup.hideGroupTitleFlag =
          this.workingCustomerService.authService.allowedCustomerUuids?.length !== 1 && !this.workingCustomerService.exploringFlag;
        this._currentFuseNavigation.push(Object.assign({}, this._navigationCustomerAndCustomerGroup));
      } else if (areaMenu === AreaMenu.CustomerMember) {
        if (laboratoryName) {
          this._navigationCustomerMember.translate = laboratoryName;
        }
        this._navigationCustomerMember.text2 = null;
        if (!this.workingCustomerService.exploringFlag) {
          this._navigationCustomerMember.text2 = this.workingCustomerService.customerName;
        }
        this._navigationCustomerMember.hideGroupTitleFlag =
          this.workingCustomerService.authService.allowedCustomerUuids?.length !== 1 && !this.workingCustomerService.exploringFlag;
        this._currentFuseNavigation.push(Object.assign({}, this._navigationCustomerMember));
      } else if (areaMenu === AreaMenu.NoArea) {
        this._currentFuseNavigation.push(Object.assign({}, this._navigationNoArea));
      }
    }

    return this._currentFuseNavigation;
  }

  public refreshClassesForNavigationLaboratory(laboratoryDemoModeFlag: boolean | null, adminOrSuperAdminFlag: boolean): void {
    const laboratoryNavigation = this._currentFuseNavigation.find((x) => x.id === 'laboratory-area');
    if (laboratoryNavigation) {
      // Add or remove specific class for laboratory navigation when laboratory is in production mode
      if (laboratoryDemoModeFlag === false && adminOrSuperAdminFlag) {
        LeftMenuService.addClasses(laboratoryNavigation, 'nav-link-explore-laboratory-production-mode');
      } else {
        LeftMenuService.removeClasses(laboratoryNavigation, 'nav-link-explore-laboratory-production-mode');
      }

      this._fuseNavigationService.updateNavigationItem('laboratory-area', laboratoryNavigation);
    }
  }

  private static addClasses(fuseNavigation: NavigationInterface, classToApply: string): void {
    let classesTable: Array<string>;
    if (fuseNavigation.classes) {
      classesTable = fuseNavigation.classes.trim().split(/[ ]+/);
      if (!classesTable.includes(classToApply)) {
        classesTable.push(classToApply);
      }
    } else {
      classesTable = [classToApply.trim()];
    }

    fuseNavigation.classes = classesTable.join(' ');

    if ((fuseNavigation.children?.length as number) > 0) {
      fuseNavigation.children?.forEach((fuseNavigationChild: NavigationInterface) => {
        this.addClasses(fuseNavigationChild, classToApply);
      });
    }
  }

  private static removeClasses(fuseNavigation: NavigationInterface, classToApply: string): void {
    if (fuseNavigation.classes && fuseNavigation.classes.includes(classToApply)) {
      fuseNavigation.classes = fuseNavigation.classes.replace(classToApply, '').replace(/[ ]+/g, ' ').trim();
    }

    if ((fuseNavigation.children?.length as number) > 0) {
      fuseNavigation.children?.forEach((fuseNavigationChild: NavigationInterface) => {
        this.removeClasses(fuseNavigationChild, classToApply);
      });
    }
  }

  private setVisibilityForLaboratoryItemNavigation(
    fuseNavigation: NavigationInterface | undefined,
    itemId: string,
    itemVisibility: boolean,
  ): void {
    if (fuseNavigation) {
      const itemNavigation = fuseNavigation.children!.find((x) => x.id === itemId);
      if (itemNavigation) {
        itemNavigation.hidden = !itemVisibility;
        this._fuseNavigationService.updateNavigationItem(itemId, itemNavigation);
      }
    }
  }
}
